import { useEffect, useRef, useState } from 'react'

import type {
  EditRequest,
  EventDetail,
  EventHit,
  EventRowEditor,
  HiddenAs,
  SameNight,
} from '~/api/types'

import s from './EditorPanel.module.css'

/**
 * "Fix this listing" — the editor's half of the event sheet, ported from
 * `_calendar_page.py`'s `editPanel` and the `wireEdit` / `wireMergeFind` pair under the
 * `-- correcting a listing --` rule.
 *
 * A feed can be wrong in a way no parser recovers from. Ticketmaster billed a Colour
 * Factory night as every name on its bill — "Sophia Stel + Iglooghost + GRRL + …" — and
 * nowhere said the night is called "PC Music Presents: Pop Crypt: Crypt Cube", because its
 * payload does not know that. A person does, and until this panel had nowhere to say it.
 *
 * Editor only, because a correction is published to everyone reading this calendar. That
 * gate is two checks rather than one here, and both are load-bearing — see `editable`.
 */

/**
 * The signed-in viewer. Local by request, and a prop rather than a lookup: the Python reads
 * `DATA.me`, a global baked into the payload by the server that rendered the page. Three
 * ports in the first wave replaced a payload global with a fresh browser-side computation
 * and shipped a different answer from the one the page was built with; a viewer is the same
 * kind of fact. It arrives with the payload or it does not arrive.
 */
export interface Me {
  id: number
  name: string
  username: string
  isEditor: boolean
}

/** `wireMergeFind`'s debounce, and its floor: one letter matches most of the calendar. */
const FIND_DEBOUNCE_MS = 180
const FIND_MIN_CHARS = 2

/** Titles, cut. One 90-character festival billing pulls the whole row out of shape. */
function short(value: string | null | undefined, n: number): string {
  const text = String(value ?? '')
  if (text.length <= n) return text
  // Break at a bill separator where there is one, so the result reads as names rather than
  // a word sliced in half.
  const cut = text.slice(0, n)
  const at = cut.lastIndexOf(' + ')
  return `${at > n / 2 ? cut.slice(0, at) : cut.trimEnd()} …`
}

/**
 * The day a merge candidate is on.
 *
 * `EventHit.when` is a raw UTC stamp — the one `when` on the API the server does NOT
 * pre-format — so this formats it, as the Python's `dayLabel` does, from the browser's zone.
 * Every other date in the app arrives formatted in Europe/London and must not be re-derived;
 * this one has no formatted form to use. See the port report.
 */
function dayLabel(iso: string): string {
  const on = new Date(iso)
  if (Number.isNaN(on.getTime())) return iso.slice(0, 10)
  return on.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

/**
 * The event row, but only if it is the editor's copy of it.
 *
 * `GET /event/<id>` deletes `_EDITOR_ONLY` — `title_override`, `festival_override`,
 * `feed_title`, `feed_notes`, `deleted_at`, `hidden_as`, `superseded_by_event_id`,
 * `curation_status` — for anyone who is not an editor, and empties `same_night` with them.
 * So a payload without those fields is not an event with no corrections on it; it is
 * somebody else's payload. Rendering the panel from it would put the feed's own title in a
 * box labelled "Name", print "Saved corrections survive every future ingest run." over a
 * Save that answers 403, and offer a refuse row to a reader who cannot refuse anything.
 */
function editable(detail: EventDetail): EventRowEditor | null {
  const row = detail.event
  return 'title_override' in row && 'superseded_by_event_id' in row ? row : null
}

export interface EditorPanelProps {
  /** `DATA.me`. The panel is not rendered at all for anyone who is not the editor. */
  me: Me | null
  /** `GET /event/<id>`. Must be the editor's copy; see `editable`. */
  event: EventDetail
  /**
   * `detail.same_night` — possible duplicates, hoisted to a prop by the caller. Emptied
   * server-side for non-editors, which is the other half of the same gate.
   */
  sameNight: SameNight[]
  /** POST /edit. An empty string clears an override; `null` means "leave it alone". */
  onSave(patch: Omit<EditRequest, 'id'>): void
  /** POST /merge. An id folds this listing into that one; `null` separates them again. */
  onMerge(intoId: string | null): void
  /**
   * POST /delete with a reason. `'play'` files the whole run — Harry Potter plays the
   * Palace 85 nights. `'deleted'` is the endpoint's default and goes through `onDelete`.
   */
  onRefuse(reason: HiddenAs): void
  /** POST /delete — a row that is not an event at all. */
  onDelete(): void
  /**
   * POST /delete `{deleted: false}`. The brief named four callbacks for five controls; the
   * refuse row's third control is the one that puts a hidden listing back, and dropping it
   * would drop the only way to undo the two above. See the port report.
   */
  onRestore(): void
  /** `GET /find?kind=events&q=` — editor only, 403 for anyone else. */
  onFind(query: string): Promise<EventHit[]>
  /**
   * Open one of the same-night candidates in the sheet.
   *
   * The pile used to be drawn twice, 333px apart, and each copy was half a control: the one
   * above had titles you could press to go and look, and no way to fold; this one had the
   * fold and titles that were plain text. So confirming that two listings really are one
   * night meant reading a truncated title and pressing a destructive button on faith.
   */
  onOpen(id: string): void
}

export function EditorPanel({
  me,
  event,
  sameNight,
  onSave,
  onMerge,
  onRefuse,
  onDelete,
  onRestore,
  onFind,
  onOpen,
}: EditorPanelProps) {
  const row = editable(event)

  /*
   * The two boxes, seeded from the row and re-seeded when the row changes underneath them.
   * The Python re-renders the whole sheet after every write, so the inputs it draws always
   * carry the saved value; React keeps state across that re-render unless it is told not
   * to. Without the reset, saving a correction and pressing Undo would send the feed's
   * words to the server and leave the old override on screen as though it had survived.
   */
  const seededTitle = row ? row.title_override || row.title : ''
  const seededFest = row ? row.festival_override || row.feed_notes || '' : ''
  const seed = `${row?.id ?? ''}\u0000${seededTitle}\u0000${seededFest}`
  const [draft, setDraft] = useState({ seed, title: seededTitle, festival: seededFest })
  if (draft.seed !== seed) setDraft({ seed, title: seededTitle, festival: seededFest })

  const [query, setQuery] = useState('')
  /** `null` is the hidden list; `[]` is "searched, and nothing matched". */
  const [hits, setHits] = useState<EventHit[] | null>(null)

  // The latest `onFind` without making it a dependency: an inline arrow from the caller is
  // a new function on every render, and the search would refire on every keystroke typed
  // into any other box on the sheet.
  const find = useRef(onFind)
  useEffect(() => {
    find.current = onFind
  }, [onFind])

  const id = row?.id
  useEffect(() => {
    const asked = query.trim()
    if (asked.length < FIND_MIN_CHARS || id === undefined) {
      setHits(null)
      return
    }
    let live = true
    const timer = setTimeout(() => {
      void find.current(asked)
        .then(found => {
          // The hits on screen belong to the query that asked for them. The cleanup below
          // runs before the next query's effect, so a slow answer to an abandoned query
          // cannot land under the text that replaced it — the sibling act picker in the
          // Python guards this explicitly and says why; the merge picker never did.
          if (!live) return
          // Never itself. Folding a listing into itself is a loop nobody should be asked
          // to make sense of, and the id is in the calendar's own search index.
          setHits(found.filter(hit => hit.id !== id))
        })
        // A search that failed is not a search that found nothing, so the list closes
        // rather than saying "Nothing by that name." over a request that never landed.
        // Reporting the failure belongs to whoever owns the request; catching it here only
        // keeps a rejected promise from taking the sheet down with it.
        .catch(() => {
          if (live) setHits(null)
        })
    }, FIND_DEBOUNCE_MS)
    return () => {
      live = false
      clearTimeout(timer)
    }
  }, [query, id])

  // Two gates rather than one, so a fourth control cannot be added on the wrong side of
  // either: `me` says whether this reader may correct anything, `row` says whether the
  // server actually sent the fields a correction is made of.
  if (!me?.isEditor || !row) return null

  const merged = row.superseded_by_event_id
  const corrected = Boolean(row.title_override || row.festival_override)

  return (
    <div className={`${s.sec} ${s.edit}`}>
      <h3>Fix this listing</h3>

      <label>
        Name
        <input
          value={draft.title}
          onChange={ev => setDraft({ ...draft, title: ev.target.value })}
        />
      </label>
      <label>
        Brand or night
        <input
          value={draft.festival}
          onChange={ev => setDraft({ ...draft, festival: ev.target.value })}
          placeholder="the promoter or club night, if it runs under one"
        />
      </label>

      <div className={s.editrow}>
        <button
          type="button"
          className={`${s.bact} ${s.go}`}
          onClick={() => onSave({ title: draft.title, festival: draft.festival })}
        >
          Save
        </button>
        {corrected ? (
          <button
            type="button"
            className={s.bact}
            // An empty string is what clears an override — `null` would mean "leave it
            // alone", which is the one thing Undo must not do.
            onClick={() => onSave({ title: '', festival: '' })}
          >
            Undo &mdash; use the feed&rsquo;s words
          </button>
        ) : null}
        <span className={s.editnote}>
          {row.title_override
            ? `The feed calls it “${row.feed_title}”.`
            : 'Saved corrections survive every future ingest run.'}
        </span>
      </div>

      {merged ? (
        <div className={s.editrow}>
          <span className={s.editnote}>
            Folded into another event, so it is off the calendar.
          </span>
          <button type="button" className={s.bact} onClick={() => onMerge(null)}>
            Separate them again
          </button>
        </div>
      ) : (
        <>
          {sameNight.length ? (
            <div className={s.dupes}>
              <div className={s.editnote}>
                On the same night &mdash; if one of these is the same event, fold this one
                into it:
              </div>
              {sameNight.map(dupe => (
                <div className={s.editrow} key={dupe.id}>
                  {/* Pressable, so the candidate can be read before it is folded into. */}
                  <button type="button" className={s.dupe} onClick={() => onOpen(dupe.id)}>
                    {short(dupe.title, 52)}
                    {dupe.elsewhere ? <i>{dupe.venue}</i> : null}
                  </button>
                  {dupe.merged_into ? (
                    <span className={s.editnote}>already folded away</span>
                  ) : (
                    <button
                      type="button"
                      className={s.bact}
                      /*
                       * "Same event" is what the control has always said, and on its own it
                       * says neither which way the fold goes nor what becomes of the listing
                       * you are reading. A merge is not reversible from here: this row goes
                       * off the calendar and the sheet moves to the survivor, so "Separate
                       * them again" is on a panel you would have to go and find. The
                       * accessible name says the whole thing; the visible label stays short.
                       */
                      aria-label={foldLabel(dupe.title)}
                      title={foldLabel(dupe.title)}
                      onClick={() => onMerge(dupe.id)}
                    >
                      Same event
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : null}

          {/*
           * Folding two listings that are not on the same night at the same venue. The
           * suggestions above only reach that far, and the cases they miss are real: a gig
           * one feed dated a day out, two rows a person can see are one and no rule can.
           */}
          <div className={`${s.editrow} ${s.addrow}`}>
            <input
              className={s.mergefind}
              value={query}
              onChange={ev => setQuery(ev.target.value)}
              placeholder="…or find any other listing to fold this into"
              aria-label="Find any other listing to fold this into"
              autoComplete="off"
            />
            <div className={s.findlist} hidden={hits === null}>
              {hits === null ? null : hits.length ? (
                hits.map(hit => (
                  <button
                    type="button"
                    key={hit.id}
                    aria-label={foldLabel(hit.title, hit.venue)}
                    onClick={() => onMerge(hit.id)}
                  >
                    <span>{short(hit.title, 46)}</span>
                    <span className={s.c}>
                      {`${dayLabel(hit.when)} · ${short(hit.venue, 22)}`}
                      {hit.merged_into ? ' · folded' : ''}
                    </span>
                  </button>
                ))
              ) : (
                <div className={s.none}>Nothing by that name.</div>
              )}
            </div>
          </div>
        </>
      )}

      {/* `refuse`, never `gone`: `.gone` is a dropped act and carries a line-through, which
          draws across every descendant. That shipped, and both buttons below and the
          sentence explaining them came out struck through. */}
      <div className={`${s.editrow} ${s.refuse}`}>
        {row.deleted_at ? (
          <>
            <button type="button" className={s.bact} onClick={onRestore}>
              Put it back on the calendar
            </button>
            <span className={s.editnote}>
              {row.hidden_as === 'play'
                ? 'Filed as a play, so every night of the run is hidden with it.'
                : 'Hidden — the feeds keep sending it and nobody sees it.'}
            </span>
          </>
        ) : (
          <>
            {/* Confirmed, because these are the edits on this panel that take something off
                the calendar rather than changing how it reads. */}
            <button
              type="button"
              className={`${s.bact} ${s.bad}`}
              onClick={() => {
                if (
                  window.confirm(
                    'Hide this listing? It stays hidden however often the feed sends it back.',
                  )
                ) {
                  onDelete()
                }
              }}
            >
              Not a real event
            </button>
            <button
              type="button"
              className={`${s.bact} ${s.bad}`}
              onClick={() => {
                if (
                  window.confirm(
                    'File this as a play? Every night of the run is hidden with it.',
                  )
                ) {
                  onRefuse('play')
                }
              }}
            >
              It&rsquo;s a play, not a gig
            </button>
            <span className={s.editnote}>
              The first is for rows that are not events at all &mdash; a promoter&rsquo;s
              test, a duplicate nothing folds. The second files the whole run: Harry Potter
              plays the Palace 85 nights and you should only have to say so once.
            </span>
          </>
        )}
      </div>
    </div>
  )
}

/** What the fold will do, said in full, for the control that will do it. */
function foldLabel(title: string, venue?: string): string {
  const what = venue ? `“${title}” at ${venue}` : `“${title}”`
  return `Fold this listing into ${what} — this one comes off the calendar`
}
